import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from "@tanstack/react-table";
import {
  Table,
  TableHeader,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
} from "../../@/components/ui/table";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import {
  formatDate,
  tokenDecoder,
  truncateString,
  useDecodedTokenResult,
} from "../../lib/utils";
import SkeletonTableLoading from "../../skeletonLoading/SkeletonTableLoading";
import { Input } from "../../@/components/ui/input";
import Pagination from "../../components/Pagination";
import {
  useGetCouponStatsMutation,
  useGetCouponTypesMutation,
  useGetAllVendorNameOrIdQuery,
} from "../../api/api";
import useDebounce from "../../lib/utils";
import { ArrowUpDown, MoveDown, MoveUp } from "lucide-react";
import { FiSearch } from "react-icons/fi";
import { AddEditCouponDialog } from "./AddEditCouponDialog";
import { formatDateTime } from "../../lib/utils";
import { toast } from "../../@/components/ui/use-toast";
import FilterDropdowns from "./FilterDropdowns";
import SingleDatePicker from "../../components/SingleDatePicker";

declare module "@tanstack/table-core" {
  interface ColumnMeta<TData extends unknown, TValue> {
    isTooltip?: boolean;
  }
}

interface CouponTag {
  id: number;
  vendorId: number;
  vendorName: string;
  code: string;
  timesUsed: number;
  maxUsageCount: number;
  expiryDate: Date;
  couponTypeName: string;
  couponTypeId: number;
  couponStatusId?: number;
  couponValue: number;
  statusId: number;
  discountType: "PERCENTAGE";
  discountValue: number;
  description: string;
}

interface CouponTypes {
  description: string;
  name: string;
  id: number;
}

const CouponTagsConfiguration: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchQuery = useDebounce(searchTerm, 500);
  const [data, setData] = useState<CouponTag[]>([]);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const [endDate, setEndDate] = useState<Date | undefined>(undefined);
  const [dateError, setDateError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const token = useSelector((state: any) => state.auth.token);
  const decodedToken = tokenDecoder(token);
  const { vendorId, role } = useDecodedTokenResult();
  const [selectedVendorName, setSelectedVendorName] = useState("");
  const [selectedCouponType, setSelectedCouponType] = useState("");
  const [selectedCouponTypeName, setSelectedCouponTypeName] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedStatusName, setSelectedStatusName] = useState("");

  const { ...getAllVendor } = useGetAllVendorNameOrIdQuery(undefined, {
    skip: role === "ADMIN" ? false : true,
  });
  const [selectedVendor, setSelectedVendor] = useState("");

  const [sortingOrder, setSortingOrder] = useState<"asc" | "desc" | undefined>(
    "asc"
  );
  const [sortingColumn, setSortingColumn] = useState<string | undefined>(
    "code"
  );
  const [couponTypesData, setCouponTypesData] = useState<CouponTypes[]>([]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (pageNumber !== 1) {
      setPageNumber(1);
    }
    setSearchTerm(event.target.value);
  };

  const [getAllCouponTags, { error, isLoading }] = useGetCouponStatsMutation();
  const [getAllCouponTypes, { isLoading: typeserr }] =
    useGetCouponTypesMutation();

  const handleCouponTypeChange = (value: string) => {
    const couponTypeName =
      couponTypesData.find((type) => type.id === parseInt(value))?.name || "";

    if (value === "-1") {
      setSelectedCouponTypeName("");
      setSelectedCouponType("");
    } else {
      setSelectedCouponTypeName(
        truncateString(couponTypeName.toLowerCase(), 15)
      );
      setSelectedCouponType(value);
    }
    setPageNumber(1);
  };

  const statusOptions = [
    { value: "18", label: "Active" },
    { value: "19", label: "Inactive" },
  ];

  const handleStatusChange = (value: string) => {
    if (value === "-1") {
      setSelectedStatusName("");
      setSelectedStatus("");
    } else {
      const statusName =
        statusOptions.find((status) => status.value === value)?.label || "";
      setSelectedStatusName(statusName);
      setSelectedStatus(value);
    }
    setPageNumber(1);
  };

  const fetchCouponData = useCallback(async () => {
    try {
      const vendorId = decodedToken.vendorId;

      const isAdmin = decodedToken.role === "ADMIN";

      const response: any = await getAllCouponTags({
        getCouponStatsDto: {
          searchQuery:
            debouncedSearchQuery !== "" ? debouncedSearchQuery : undefined,
          startDate: startDate
            ? formatDateTime(startDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          endDate: endDate
            ? formatDateTime(endDate, "YYYY-MM-DDTHH:mm:ssZ")
            : undefined,
          pageSize: 10,
          pageNumber: pageNumber,
          sortingColumn: sortingColumn,
          sortingOrder: sortingOrder,
          vendorId: selectedVendor
            ? parseInt(selectedVendor)
            : isAdmin
            ? undefined
            : vendorId,
          couponTypeId: selectedCouponType
            ? parseInt(selectedCouponType)
            : undefined,
          couponStatusId: selectedStatus ? parseInt(selectedStatus) : undefined,
        },
      }).unwrap();
      setData(response.data.coupons);
      const typesResponse: any = await getAllCouponTypes().unwrap();

      const couponTypes =
        decodedToken.role === "VENDOR"
          ? typesResponse.data.filter((coupon: any) => coupon.id !== 2)
          : typesResponse.data;

      setCouponTypesData(couponTypes);
      setTotalCount(response.data.totalCount || 0);
    } catch (err) {
      console.error("Failed to get Coupon:", err);
    }
  }, [
    getAllCouponTags,
    debouncedSearchQuery,
    pageNumber,
    sortingColumn,
    sortingOrder,
    startDate,
    endDate,
    selectedVendor,
    selectedCouponType,
    selectedStatus,
  ]);

  useEffect(() => {
    if (!dateError) {
      fetchCouponData();
    }
  }, [fetchCouponData, dateError, selectedVendor, startDate, endDate]);

  const handlePageChange = (newPageNumber: number) => {
    if (newPageNumber < 0 || newPageNumber > Math.ceil(totalCount / pageSize)) {
      return;
    }
    setPageNumber(newPageNumber);
  };

  const handleSorting = (column: string) => {
    if (column === "expiryDate") {
      if (sortingColumn === column) {
        setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
      } else {
        setSortingColumn(column);
        setSortingOrder("asc");
      }
    } else {
      if (sortingColumn === column) {
        setSortingOrder(sortingOrder === "asc" ? "desc" : "asc");
      } else {
        setSortingColumn(column);
        setSortingOrder("asc");
      }
    }
    setPageNumber(1);
  };

  const createSortableHeader = (title: string, columnKey: string) => (
    <div
      onClick={() => handleSorting(columnKey)}
      className="cursor-pointer flex gap-1 items-center"
    >
      {title}{" "}
      {sortingColumn === columnKey ? (
        sortingOrder === "asc" ? (
          <MoveUp size={14} />
        ) : (
          <MoveDown size={14} />
        )
      ) : (
        <ArrowUpDown size={14} />
      )}
    </div>
  );

  const createColumn = (
    key: keyof CouponTag,
    title: string,
    size: number,
    customCell?: (row: CouponTag) => React.ReactNode
  ): ColumnDef<CouponTag> => ({
    accessorKey: key,
    header: () => createSortableHeader(title, key),
    size,
    cell: ({ row }) => (
      <span>
        {customCell ? customCell(row.original) : row.original[key]?.toString()}
      </span>
    ),
  });

  const columns = useMemo<ColumnDef<CouponTag>[]>(() => {
    const baseColumns: ColumnDef<CouponTag>[] = [
      createColumn("code", "Code", 100, (row) => {
        return (
          <div className="relative group truncate">
            <span>{row.code}</span>
          </div>
        );
      }),
      {
        accessorKey: "couponTypeName",
        header: () => createSortableHeader("Type", "couponTypeName"),
        size: 130,
        cell: ({ row }) => {
          return (
            <div className="relative group truncate">
              <span>{row.original.couponTypeName.toLowerCase()}</span>
            </div>
          );
        },
      },
      {
        accessorKey: "expiryDate",
        header: () => createSortableHeader("Expiry Date", "expiryDate"),
        size: 150,
        cell: ({ row }) => (
          <span>
            {row.original.expiryDate
              ? formatDate(row.original.expiryDate, "dd MMM yyyy")
              : "N/A"}
          </span>
        ),
      },
      createColumn("couponValue", "Redeemable Value", 170),
      createColumn("maxUsageCount", "Max Usage", 120),
      createColumn("timesUsed", "Times Used", 130),
      createColumn("discountValue", "Discount Value", 150),
    ];

    // Conditionally include the "Vendor Name" column for ADMIN role
    if (decodedToken.role === "ADMIN") {
      baseColumns.push(
        createColumn("vendorName", "Vendor", 100, (row) => {
          const truncatedName =
            row.vendorId === 0 || !row.vendorName ? "All" : row.vendorName;

          return (
            <div className="relative group">
              <span>{truncatedName}</span>
            </div>
          );
        })
      );
    }

    // Add the actions column
    baseColumns.push({
      id: "actions",
      header: () => (
        <div className="flex items-center justify-center">Action</div>
      ),
      size: 80,
      meta: {
        isTooltip: false,
      },
      cell: ({ row }) => {
        const isDisabled =
          decodedToken.role === "ADMIN" &&
          (row.original.vendorId !== null ||
            row.original.vendorId === undefined);

        return (
          <div
            className={`flex items-center justify-center ${
              isDisabled ? "pointer-events-none opacity-50" : ""
            }`}
          >
            <AddEditCouponDialog
              item={row.original}
              isEditOnly={true}
              vendorId={
                decodedToken.role === "VENDOR" ? decodedToken.vendorId : null
              }
              fetchCouponData={fetchCouponData}
              setPageNumber={setPageNumber}
              setSearchTerm={setSearchTerm}
              couponTypesData={couponTypesData}
              fetchCouponTypes={async () => {
                const response = await getAllCouponTypes().unwrap();
                return response.data;
              }}
            />
          </div>
        );
      },
    });

    return baseColumns;
  }, [
    sortingColumn,
    sortingOrder,
    setPageNumber,
    setSearchTerm,
    fetchCouponData,
    decodedToken.role,
  ]);
  const vendorList = getAllVendor.data?.data || [];
  const handleVendorChange = (value: string) => {
    const vendorName =
      vendorList.find((vendor) => vendor.id === parseInt(value))?.company || "";

    if (value === "-1") {
      setSelectedVendor("");
      setSelectedVendorName("");
    } else {
      setSelectedVendorName(truncateString(vendorName, 15));
      setSelectedVendor(value);
    }
    setPageNumber(1);
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    pageCount: Math.ceil(totalCount / pageSize),
    manualPagination: true,
    state: {
      pagination: {
        pageIndex: pageNumber - 1,
        pageSize,
      },
    },
  });

  if (error) {
    console.error("Error fetching Coupon tags:", error);
    return <div>Error loading Coupon tags. Please try again later.</div>;
  }

  return (
    <div className="overflow-hidden h-full ">
      <div className="flex justify-between items-center flex-wrap mb-2 gap-2">
        <div className="flex justify-between items-start gap-y-2 gap-x-10 w-full flex-col sm:flex-row">
          <div className="w-full sm:w-auto">
            <AddEditCouponDialog
              isEditOnly={false}
              fetchCouponData={fetchCouponData}
              vendorId={
                decodedToken.role === "VENDOR" ? decodedToken.vendorId : null
              }
              setPageNumber={setPageNumber}
              setSearchTerm={setSearchTerm}
              fetchCouponTypes={async () => {
                const response = await getAllCouponTypes().unwrap();
                return response.data;
              }}
            />
          </div>

          <div className="flex gap-2 flex-wrap">
            <div className="w-full sm:w-auto">
              <SingleDatePicker
                startDate={startDate}
                endDate={endDate}
                onStartDateSelect={setStartDate}
                onEndDateSelect={setEndDate}
              />
            </div>

            <div className="w-full sm:w-auto">
              <FilterDropdowns
                selectedVendorName={selectedVendorName}
                handleVendorChange={handleVendorChange}
                vendorList={vendorList}
                showVendorDropdown={decodedToken.role === "ADMIN"}
                selectedCouponTypeName={selectedCouponTypeName}
                handleCouponTypeChange={handleCouponTypeChange}
                couponTypesData={couponTypesData}
                selectedStatusName={selectedStatusName}
                handleStatusChange={handleStatusChange}
              />
            </div>

            <div className="relative flex justify-center text-customGrayText items-center w-full sm:w-48">
              <FiSearch className="absolute left-2 text-lg" />
              <Input
                placeholder="Search Coupon"
                className="text-sm text-left h-9 pl-8"
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </div>
      </div>
      {isLoading ? (
        <div className="overflow-y-auto h-[calc(100vh-14rem)]">
          <div className="w-full rounded-lg shadow bg-white h-full">
            <Table>
              <TableHeader className="bg-customGreenColor text-white">
                {table.getHeaderGroups().map((headerGroup) => (
                  <TableRow key={headerGroup.id}>
                    {headerGroup.headers.map((header) => (
                      <TableCell
                        key={header.id}
                        className="font-medium text-sm"
                        style={{
                          minWidth: header.column.columnDef.size,
                          maxWidth: header.column.columnDef.size,
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHeader>
            </Table>
            <SkeletonTableLoading />
          </div>
        </div>
      ) : error ? (
        <div>Error loading Coupon tags.</div>
      ) : (
        <>
          <div className="overflow-y-auto h-[calc(100vh-14rem)]">
            <div className="overflow-x-auto w-full rounded-lg shadow bg-white h-full">
              <Table>
                <TableHeader
                  className="bg-customGreenColor text-white sticky top-0"
                  style={{ zIndex: 9 }}
                >
                  {table.getHeaderGroups().map((headerGroup) => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <TableHead
                          key={header.id}
                          className="font-medium text-sm"
                          style={{
                            minWidth: header.column.columnDef.size,
                            maxWidth: header.column.columnDef.size,
                          }}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length}
                        className="text-center"
                      >
                        No data available
                      </TableCell>
                    </TableRow>
                  ) : (
                    table.getRowModel().rows.map((row) => (
                      <TableRow key={row.id}>
                        {row.getVisibleCells().map((cell) => (
                          <TableCell
                            key={cell.id}
                            className="font-medium text-sm whitespace-nowrap overflow-hidden"
                            style={{
                              minWidth: cell.column.columnDef.size,
                              maxWidth: cell.column.columnDef.size,
                            }}
                            isTooltip={
                              cell.column.columnDef.meta?.isTooltip ?? true
                            }
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext()
                            )}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </>
      )}
      {data.length > 0 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-2 pt-4 pb-4 overflow-hidden">
          <div className="text-[#667085]">
            Page {table.getState().pagination.pageIndex + 1} of{" "}
            {Math.ceil(totalCount / pageSize)}
          </div>
          <Pagination
            totalCount={totalCount}
            pageSize={pageSize}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default CouponTagsConfiguration;
